import React, { useState } from 'react'
import { Code2, Brain, BarChart, Cloud, Database, WrapText, ChevronLeft, ChevronRight } from 'lucide-react'
import { GradientContainer } from '../components'

interface SkillItemProps {
  icon: React.ReactNode
  title: string
  description: string
  skills: string[]
}

const SkillItem: React.FC<SkillItemProps> = ({ icon, title, description, skills }) => {
  return (
    <div className="mb-8 p-4 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all duration-300">
      <div className="flex items-center gap-3 mb-3">
        <div className="text-white">
          {icon}
        </div>
        <h3 className="text-lg md:text-xl font-semibold text-white">{title}</h3>
      </div>
      <p className="text-white text-sm md:text-base mb-4 leading-relaxed">{description}</p>
      <div className="flex flex-wrap gap-2">
        {skills.map((skill, index) => (
          <span 
            key={index}
            className="px-3 py-1 bg-blue-500/20 text-white text-xs md:text-sm rounded-full border border-blue-500/30"
          >
            {skill}
          </span>
        ))}
      </div>
    </div>
  )
}

const FullStackDeveloper: React.FC = () => {
  const fullStackSkills = [
    {
      icon: <Code2 size={20} />,
      title: "Frontend Development",
      description: "Creating responsive, interactive user interfaces with modern frameworks and clean, maintainable code.",
      skills: ["React.js", "Next.js", "TypeScript", "Tailwind CSS", "HTML5", "CSS3"]
    },
    {
      icon: <Database size={20} />,
      title: "Backend & Database",
      description: "Building scalable server-side applications with robust database design and API development.",
      skills: ["Node.js", "Express.js", "Python", "PostgreSQL", "MongoDB", "Firebase"]
    },
    {
      icon: <Cloud size={20} />,
      title: "Cloud & DevOps",
      description: "Deploying and managing applications using cloud platforms and containerization technologies.",
      skills: ["AWS", "Docker", "Kubernetes", "GCP", "Firebase", "Git"]
    }
  ]

  return (
    <div className='text-white m-4 md:m-8 h-full pt-16 md:pt-8'>
      {/* Title */}
      <h2 className="text-sm md:text-lg mb-6 pt-safe-top md:pt-0">Full Stack Developer</h2>
      
      {/* Content container with proper scrolling */}
      <div className="h-full pb-8 md:pb-20 overflow-y-auto pb-safe-bottom hide-scrollbar"
           style={{ 
             height: 'calc(100% - 6rem)',
             WebkitOverflowScrolling: 'touch',
             maxHeight: 'calc(100dvh - 12rem)'
           }}>
        
        {/* Bio Section */}
        <div className="mb-6 text-white text-sm md:text-base leading-relaxed">
          <p>
            I love crafting seamless user experiences and robust backend systems that solve real-world problems. 
            My approach combines clean, maintainable code with modern development practices.
          </p>
        </div>

        {/* Skills */}
        {fullStackSkills.map((skill, index) => (
          <SkillItem
            key={index}
            icon={skill.icon}
            title={skill.title}
            description={skill.description}
            skills={skill.skills}
          />
        ))}
      </div>
    </div>
  )
}

const AIEngineer: React.FC = () => {
  const aiSkills = [
    {
      icon: <Brain size={20} />,
      title: "Machine Learning",
      description: "Developing intelligent systems using supervised and unsupervised learning algorithms with deep understanding of neural networks.",
      skills: ["PyTorch", "TensorFlow", "Scikit-learn", "NumPy", "Pandas"]
    },
    {
      icon: <WrapText size={20} />,
      title: "Deep Learning & NLP",
      description: "Building neural networks from scratch and implementing advanced NLP models for semantic understanding and text processing.",
      skills: ["Transformers", "LSTMS", "Embeddings"]
    },
    {
      icon: <BarChart size={20} />,
      title: "Data Analysis & Visualization",
      description: "Extracting insights from complex datasets and creating compelling visualizations for data-driven decision making.",
      skills: ["Power BI", "Matplotlib", "DAX", "Data Mining", "Statistical Analysis"]
    }
  ]

  return (
    <div className='text-white m-4 md:m-8 h-full pt-16 md:pt-8'>
      {/* Title */}
      <h2 className="text-sm md:text-lg mb-6 pt-safe-top md:pt-0">AI Engineer</h2>
      
      {/* Content container with proper scrolling */}
      <div className="h-full pb-8 md:pb-20 overflow-y-auto pb-safe-bottom hide-scrollbar"
           style={{ 
             height: 'calc(100% - 6rem)',
             WebkitOverflowScrolling: 'touch',
             maxHeight: 'calc(100dvh - 12rem)'
           }}>
        
        {/* Bio Section */}
        <div className="mb-6">
          <p className="text-white text-sm md:text-base leading-relaxed mb-4">
            With specialized coursework in Deep Learning, NLP with Deep Learning, and Machine Learning, I focus on 
            creating intelligent systems that understand and process human language and behavior patterns.
          </p>
          <p className="text-white text-sm md:text-base leading-relaxed">
            I've led AI-driven projects including mental wellbeing solutions and implemented neural networks from 
            scratch, demonstrating both theoretical knowledge and practical application skills.
          </p>
        </div>

        {/* Skills */}
        {aiSkills.map((skill, index) => (
          <SkillItem
            key={index}
            icon={skill.icon}
            title={skill.title}
            description={skill.description}
            skills={skill.skills}
          />
        ))}
      </div>
    </div>
  )
}

// Mobile section switcher component
const MobileSectionSwitcher: React.FC = () => {
  const [currentSection, setCurrentSection] = useState(0)
  const sections = [
    { component: <FullStackDeveloper key="fullstack" />, name: "Full Stack" },
    { component: <AIEngineer key="ai" />, name: "AI Engineer" }
  ]

  const nextSection = () => {
    setCurrentSection((prev) => (prev + 1) % sections.length)
  }

  const prevSection = () => {
    setCurrentSection((prev) => (prev - 1 + sections.length) % sections.length)
  }

  return (
    <div className="h-full relative">
      {/* Navigation dots */}
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20"
           style={{ top: 'max(1rem, env(safe-area-inset-top))' }}>
        {sections.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSection(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentSection 
                ? 'bg-white' 
                : 'bg-white/40'
            }`}
          />
        ))}
      </div>

      {/* Navigation arrows */}
      <button
        onClick={prevSection}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 p-2 bg-white/10 rounded-full backdrop-blur-sm"
        style={{ left: 'max(1rem, env(safe-area-inset-left))' }}
      >
        <ChevronLeft className="text-white" size={20} />
      </button>
      
      <button
        onClick={nextSection}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 p-2 bg-white/10 rounded-full backdrop-blur-sm"
        style={{ right: 'max(1rem, env(safe-area-inset-right))' }}
      >
        <ChevronRight className="text-white" size={20} />
      </button>

      {/* Content */}
      <div className="h-full">
        {sections[currentSection].component}
      </div>
    </div>
  )
}

const About: React.FC = () => {
  return (
    <div className='bg-black p-4 md:p-8 flex flex-col'
         style={{
           height: '100dvh', // Dynamic viewport height - adjusts for iOS Safari address bar
           minHeight: '100svh' // Small viewport height - ensures minimum height when address bar is visible
         }}>
      {/* Mobile Layout */}
      <div className="block md:hidden h-full">
        <GradientContainer 
          components={[
            <MobileSectionSwitcher key="mobile-switcher" />
          ]}
        />
      </div>

      {/* Desktop Layout - 2 Columns */}
      <div className="hidden md:block h-full">
        <GradientContainer 
          components={[
            <FullStackDeveloper key="desktop-fullstack" />,
            <AIEngineer key="desktop-ai" />
          ]}
        />
      </div>

      <div className='absolute text-white opacity-30 text-xs'
           style={{
             bottom: 'max(0.5rem, env(safe-area-inset-bottom))',
             left: 'max(0.5rem, env(safe-area-inset-left))'
           }}>
        &copy; AHMED ELGHAMRINY
      </div>
    </div>
  )
}

export default About